
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, Loader, ArrowRight } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';

const HotmartSuccessPage: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [verified, setVerified] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const verifyTransaction = async () => {
            // Get transaction ID from URL params (Hotmart usually passes it)
            // URL format: /hotmart/success?txid=ABC-123 or similar
            const params = new URLSearchParams(location.search);
            const transactionId = params.get('txid') || params.get('transaction');

            if (!transactionId) {
                // If no ID, we just show a generic success message but can't verify deeply frontend-side immediately
                // without identification.
                setLoading(false);
                setVerified(true);
                return;
            }

            try {
                // Poll for transaction confirmation in our DB
                // It might take a few seconds for the webhook to arrive
                let attempts = 0;
                const maxAttempts = 10;

                const checkDb = async () => {
                    const { data } = await supabase
                        .from('hotmart_transactions')
                        .select('status')
                        .eq('transaction_id', transactionId)
                        .single();

                    if (data && (data.status === 'APPROVED' || data.status === 'COMPLETED')) {
                        setVerified(true);
                        setLoading(false);
                        toast.success('¡Pago verificado correctamente!');
                        return true;
                    }
                    return false;
                };

                const poll = setInterval(async () => {
                    attempts++;
                    const success = await checkDb();
                    if (success || attempts >= maxAttempts) {
                        clearInterval(poll);
                        setLoading(false);
                        if (!success) {
                            setVerified(true); // Assume success but manual verification might be needed
                            toast.success('Pago recibido. Tus monedas aparecerán en breve.');
                        }
                    }
                }, 2000);

            } catch (error) {
                console.error('Error verifying:', error);
                setLoading(false);
                setVerified(true);
            }
        };

        verifyTransaction();
    }, [location]);

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center p-4">
            <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
                {loading ? (
                    <div className="flex flex-col items-center">
                        <Loader className="w-16 h-16 text-blue-600 animate-spin mb-4" />
                        <h2 className="text-xl font-bold text-gray-800">Verificando tu pago...</h2>
                        <p className="text-gray-500 mt-2">Por favor espera un momento.</p>
                    </div>
                ) : (
                    <div className="flex flex-col items-center animate-in fade-in zoom-in duration-500">
                        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
                            <CheckCircle className="w-10 h-10 text-green-600" />
                        </div>

                        <h1 className="text-2xl font-bold text-gray-900 mb-2">¡Pago Exitoso!</h1>
                        <p className="text-gray-600 mb-8">
                            Gracias por tu compra. Tus VCoins han sido añadidos a tu cuenta.
                        </p>

                        <button
                            onClick={() => navigate('/dashboard')}
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-xl flex items-center justify-center gap-2 transition-colors"
                        >
                            Ir al Dashboard
                            <ArrowRight size={20} />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default HotmartSuccessPage;
